import React, { useState } from "react";
import currencyCodes from "currency-codes";
import 'currency-flags/dist/currency-flags.min.css';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
  IconButton,
  InputLabel,
  FormControl,
  TextField,
  Typography,
  Box,
  Grid,
  useMediaQuery,
  Theme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import { SelectChangeEvent } from "@mui/material";
import Button from "../ui/elements/buttons/Button";
import { Add } from "@mui/icons-material";
import { GB } from "country-flag-icons/react/3x2";
import { useCurrency } from "../modules/settings/queries/useCurrency";
import { Currency } from "../modules/settings/settingsType/Currency";
import useCreateCurrency from "../modules/settings/mutations/useCreateCurrency";
import useUpdateCurrency from "../modules/settings/mutations/useUpdateCurrency ";
import useDeleteCurrency from "../modules/settings/mutations/useDeleteCurrency";
import AddEditpopup from "../modules/settings/components/AddEditpopup";
import ConfirmDialog from "../utils/ConfirmDialog";


const Settings: React.FC = () => {
  const {
    data: currencyData,
    isLoading,
    refetch: currencyRefetch,
  } = useCurrency({rowsPerPage:100});
  const currency: Currency[] = currencyData?.data || [];
  const { mutate: addCurrency } = useCreateCurrency();
  const { mutate: updateCurrency } = useUpdateCurrency();
  const { mutate: deleteCurrency } = useDeleteCurrency();

  const [open, setOpen] = useState<boolean>(false);
  const [deleteOpen, setdeleteOpen] = useState(false);

  const [currentCurrency, setCurrentCurrency] = useState<Currency>();

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = React.useState(false);

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  // Handle opening the edit dialog
  const handleEdit = (currency: Currency) => {
    setCurrentCurrency(currency);
    setIsEditing(true);
    setOpen(true);
  };

  // Handle opening the add new currency dialog
  const handleAddNew = () => {
    setCurrentCurrency(undefined);
    setIsEditing(false);
    setOpen(true);
  };

  // Handle closing the dialog
  const handleClose = () => {
    setOpen(false);
    setCurrentCurrency(undefined);
  };

  const handleSave = () => {
    if (isSaving) return; // Prevent multiple clicks
  
    setIsSaving(true); // Disable button immediately to prevent repeated clicks
  
    try {
      if (!currentCurrency) {
        setIsSaving(false); // Re-enable button if no currency is selected
        return;
      }
  
      if (isEditing) {
        if (currentCurrency.documentId) {
          const { documentId, name, code, factor } = currentCurrency;
          updateCurrency(
            { documentId, updatedCurrency: { name, code, factor } },
            {
              onSuccess: () => {
                currencyRefetch();
                handleClose(); // Close dialog immediately after success
              },
              onError: (error) => {
                console.error("Error updating currency:", error);
              },
            }
          );
        } else {
          console.error("Error: documentId is undefined.");
        }
      } else {
        addCurrency(currentCurrency, {
          onSuccess: () => {
            currencyRefetch();
            handleClose(); // Close dialog immediately after success
          },
          onError: (error) => {
            console.error("Error adding currency:", error);
          },
        });
      }
    } finally {
      setTimeout(() => setIsSaving(false), 500); // Add slight delay before re-enabling button
    }
  };
  

  const handleDelete = (documentId: string) => {
    if (!documentId) {
      console.error("Error: documentId is missing");
      return;
    }

    deleteCurrency(documentId, {
      onSuccess: () => {
        currencyRefetch();
      },
      onError: (error) => {
        console.error("Error deleting currency:", error);
      },
    });
  };

  // Handle selecting a currency from the dropdown (Updated type)
  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const selectedCurrency = currencyCodes.data.find(
      (currency) => currency.currency === e.target.value
    );

    
  if (selectedCurrency) {
    setCurrentCurrency((prevCurrency) => ({
      ...prevCurrency,
      name: selectedCurrency.currency,
      code: selectedCurrency.code,
      factor: prevCurrency?.factor || 1, // Default factor to 1 if undefined
    }));
  }
};
  // Handle input changes for factor in the dialog
  const handleFactorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setCurrentCurrency((prevCurrency) =>
      prevCurrency
        ? { ...prevCurrency, factor: parseFloat(value) }
        : prevCurrency
    );
  };

  // Validate if the current currency has valid inputs
  const isValidCurrency = () => {
    return (
      currentCurrency?.name &&
      currentCurrency?.factor &&
      currentCurrency?.factor > 0
    );
  };

  const handleDeleteOpen = (currency: Currency) => {
    // setCurrencyToDelete(currency);
    setCurrentCurrency(currency)
    setdeleteOpen(true);
  };
  const handleDeleteClose = () => setdeleteOpen(false);

  return (
    <Box p={2}>
      <Typography variant="h4" p={2} pb={0}>
        Settings
      </Typography>
      <Box
        sx={{ padding: isMobile ? "16px 8px 0px 8px" : "24px 64px 0px 64px " }}
      >
        <Box bgcolor={"#fff"} borderRadius={"20px"} p={"10px"}>
          <Box ml={1.5}>
            <Typography variant="h6">Currencies</Typography>
            <Grid
              container
              spacing={2}
              alignItems={"center"}
              marginBottom={"10px"}
            >
              <Grid
                item
                xs={1.8}
                sm={1.8}
                md={1.8}
                display={"flex"}
                justifyContent={"center"}
              >
                 <span style={{width:'50px', height:'30px'}} className="currency-flag currency-flag-shp"></span>
              </Grid>
              <Grid item xs={2.5} sm={2.5} md={2.5}>
                <TextField
                  variant="filled"
                  value="UK Pound"
                  fullWidth
                  sx={{
                    "& .MuiInputBase-input": {
                      paddingTop: "8px",
                    },
                  }}
                  InputProps={{
                    disableUnderline: true,
                  }}
                />
              </Grid>
              <Grid item xs={2.7} sm={2.7} md={2.5}>
                <TextField
                  variant="filled"
                  value="GBP"
                  fullWidth
                  sx={{
                    "& .MuiInputBase-input": {
                      paddingTop: "8px",
                    },
                  }}
                  InputProps={{
                    disableUnderline: true,
                  }}
                />
              </Grid>
              <Grid item xs={2.5} md={2.2}>
                <TextField
                  variant="filled"
                  value="1"
                  fullWidth
                  sx={{
                    "& .MuiInputBase-input": {
                      paddingTop: "8px",
                    },
                  }}
                  InputProps={{
                    disableUnderline: true,
                  }}
                />
              </Grid>
            </Grid>
          </Box>
          <TableContainer
            sx={{
              height: isMobile ? "calc(100vh - 380px)" : "calc(100vh - 400px)",
              "&::-webkit-scrollbar": {
                width: "6px",
                height: "6px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(91, 91, 91, 1)",
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "rgba(242, 242, 242, 1)",
              },
            }}
          >
            <Table
              stickyHeader
              sx={{
                "& .MuiTableCell-root": {
                  bgcolor: "#fff",
                  border: "none",
                  padding: "0px",
                  paddingLeft: "16px",
                  fontSize:'16px '
                },
              }}
            >
              <TableHead
                sx={{
                  "& .MuiTableCell-root": {
                    bgcolor: "#fff",
                    marginTop: "8px",
                    borderBottom: "1px solid lightgrey",
                  },
                }}
              >
                <TableRow>
                  <TableCell sx={{ minWidth: "20px" }}>Sr.</TableCell>
                  <TableCell sx={{ minWidth: "40px" }}>Flag</TableCell>
                  <TableCell sx={{ minWidth: "110px" }}>Currency</TableCell>
                  <TableCell sx={{ minWidth: "110px" }}>
                    Currency Code
                  </TableCell>
                  <TableCell sx={{ minWidth: "80px" }}>Factor</TableCell>
                  <TableCell sx={{ minWidth: "120px" }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currency.length<1 ?
               <TableRow>
               <TableCell colSpan={6} align="center" style={{ height: "200px" }}>
                 <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                   No currency Added!
                 </Box>
               </TableCell>
             </TableRow> : 
                  <>
                {currency
                .filter((item) => item.code !== "GBP")
                .map((currency, index) => (
                  <TableRow key={currency.id}>
                    <TableCell>
                      {index + 1}
                      {"."}
                    </TableCell>
                    <TableCell>
                      {currency.code ? (
                        <span
                          className={`currency-flag currency-flag-${currency.code.toLowerCase()}`}
                          style={{ marginRight: "10px", width: "40px", height: "25px" }}
                        ></span>
                      ) : (
                        <Typography>No Flag</Typography>
                      )}
                    </TableCell>

                    <TableCell>{currency.name}</TableCell>
                    <TableCell>{currency.code}</TableCell>
                    <TableCell>{currency.factor}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEdit(currency)}>
                        <Typography fontSize={'18px'}>Edit</Typography>
                      </IconButton>
                      <IconButton>
                        <Typography>|</Typography>
                      </IconButton>
                      <IconButton onClick={() => handleDeleteOpen(currency)}>
                        <DeleteIcon
                          sx={{ color: "#F6333F", fontSize: "20px" }}
                        />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                </>
                }
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
      <Box
        display={"flex"}
        justifyContent={"flex-end"}
        p={2}
        pb={0}
        sx={{ marginRight: isMobile ? "5px" : "60px" }}
      >
        <Button
          sx={{ border: "1px solid black", height: "30px" }}
          background={"#fff"}
          color={"#000"}
          endIconn={<Add />}
          onClick={handleAddNew}
        >
          Add Currency
        </Button>
      </Box>
      <AddEditpopup
        open={open}
        handleClose={handleClose}
        isEditing={isEditing}
        currentCurrency={currentCurrency}
        handleSelectChange={handleSelectChange}
        handleFactorChange={handleFactorChange}
        handleSave={handleSave}
        isValidCurrency={isValidCurrency}
        currencyData={currency}
        isSaving={isSaving}
      />

<ConfirmDialog
  open={deleteOpen}
  onClose={handleDeleteClose}
  onConfirm={() => {
    if (currentCurrency?.documentId) {
      handleDelete(currentCurrency.documentId);
      handleDeleteClose(); // Close after deleting
    }
  }}
  message="Are you sure you want to delete? This action cannot be undone."
  confirmText="Yes"
  cancelText="No"
/>
    </Box>
  );
};

export default Settings;
